import { useState, useEffect, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import { request, errorText } from '@papan/api-client'
import { useTheme, getThemeClass } from './ThemeProvider'
import { useToast } from './Toast'

interface UserEmail {
  id: string
  email: string
  isPrimary: boolean
  verifiedAt: string | null
}

interface OrgCreateDialogProps {
  onCreated: (tenantId: string, name: string, ownerEmail: string) => void
  onClose: () => void
  /** Pre-populated primary email (avoids API call for single-email users) */
  userEmail?: string
}

export function OrgCreateDialog({ onCreated, onClose, userEmail }: OrgCreateDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const nameRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [emails, setEmails] = useState<UserEmail[]>([])
  const [ownerEmail, setOwnerEmail] = useState(userEmail || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    nameRef.current?.focus()
    // Fetch user's emails (for multi-email picker)
    request<{ emails: UserEmail[] }>('/api/emails')
      .then(data => {
        setEmails(data.emails)
        if (!ownerEmail) {
          const primary = data.emails.find(e => e.isPrimary)
          if (primary) setOwnerEmail(primary.email)
          else if (data.emails.length > 0) setOwnerEmail(data.emails[0].email)
        }
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleCreate = async () => {
    if (!name.trim() || !ownerEmail) return
    setSaving(true)
    setError('')
    try {
      const result = await request<{ ok: boolean; tenantId: string; name: string }>('/api/orgs', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), ownerEmail }),
        headers: { 'Content-Type': 'application/json' },
      })
      toast.success(`Organization "${result.name}" created`)
      onCreated(result.tenantId, result.name, ownerEmail)
    } catch (err: any) {
      const text = errorText(err, 'Failed to create organization')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}>
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold themed-text">New Organization</h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Organization name */}
          <div>
            <label className="text-sm themed-text-secondary block mb-1">Name</label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError('') }}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
              placeholder="My Organization"
              maxLength={100}
              className="w-full themed-input border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>

          {/* Owner email */}
          <div>
            <label className="text-sm themed-text-secondary block mb-1">Owner email</label>
            {emails.length <= 1 ? (
              <div className="themed-text text-sm py-1">{ownerEmail || <span className="themed-text-muted">Loading...</span>}</div>
            ) : (
              <select
                value={ownerEmail}
                onChange={e => setOwnerEmail(e.target.value)}
                className="w-full themed-input border rounded-md px-3 py-2 text-sm outline-none"
              >
                {emails.map(e => (
                  <option key={e.id} value={e.email}>
                    {e.email}{e.isPrimary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs themed-text-muted mt-1">
              This email will be the owner identity for this organization.
            </p>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <div className="px-6 pb-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !name.trim() || !ownerEmail}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}
